import React, { useCallback, useState, useEffect } from "react";
import StickyAction from "../elements/StickyAction";
import {
  getWrapperStyle,
  getInnerStyle,
  getGapY,
  useViewMode,
  VIEW_MODES,
} from "../elements/ViewModeSelect";
import { useItemSize, getSizeScale, ItemSizeType, ITEM_SIZES } from "../elements/SizeSelect";

interface MultipleGeneratorLayoutProps {
  storageKey: string;
  defaultData: string[];
  maxItems: number;
  itemName: string;
  confirmClearMessage: string;
  RenderComponent: React.FC<{
    data: string;
    index: number;
    itemSize?: ItemSizeType;
    onDataChange: (index: number, value: string) => void;
    onClear: () => void;
    showRemove: boolean;
    onRemove: () => void;
  }>;
}

const GeneratorList: React.FC<{
  viewMode: string;
  itemSize: ItemSizeType;
  dataList: string[];
  updateData: (index: number, value: string) => void;
  clearSingleData: (index: number) => void;
  removeData: (index: number) => void;
  RenderComponent: MultipleGeneratorLayoutProps["RenderComponent"];
}> = ({
  viewMode,
  itemSize,
  dataList,
  updateData,
  clearSingleData,
  removeData,
  RenderComponent,
}) => {
  const baseGap = parseInt(getGapY(viewMode), 10) || 50;
  const gapY = Math.round(baseGap * getSizeScale(itemSize)) + "px";

  const getGridTemplateColumns = (mode: string, size: ItemSizeType) => {
    let baseWidth = 270;
    switch (mode) {
      case VIEW_MODES.WIDEST:
        baseWidth = 480;
        break;
      case VIEW_MODES.WIDER:
        baseWidth = 420;
        break;
      case VIEW_MODES.WIDE:
        baseWidth = 360;
        break;
      case VIEW_MODES.NORMAL:
      default:
        baseWidth = 270;
        break;
    }
    if (mode === VIEW_MODES.NORMAL) {
      if (size === ITEM_SIZES.EXTRA_SMALL) return "repeat(auto-fill, minmax(150px, 1fr))";
      if (size === ITEM_SIZES.SMALL) return "repeat(auto-fill, minmax(200px, 1fr))";
      if (size === ITEM_SIZES.LARGE) return "repeat(auto-fill, minmax(340px, 1fr))";
      return "repeat(auto-fill, minmax(270px, 1fr))";
    }
    const scale = getSizeScale(size);
    const finalWidth = Math.round(baseWidth * scale);
    return `repeat(auto-fill, minmax(${finalWidth}px, 1fr))`;
  };
  const gridTemplateColumns = getGridTemplateColumns(viewMode, itemSize);

  return (
    <div style={getWrapperStyle(viewMode)}>
      <div
        style={{
          ...getInnerStyle(viewMode),
          ["--result-scale" as any]: getSizeScale(itemSize),
        }}
      >
        <div
          className="w-100"
          style={{
            display: "grid",
            gridTemplateColumns,
            gap: gapY,
          }}
        >
          {dataList.map((data, index) => (
            <div key={index} className="h-full">
              <RenderComponent
                data={data}
                index={index}
                itemSize={itemSize}
                onDataChange={updateData}
                onClear={() => clearSingleData(index)}
                showRemove={dataList.length > 1}
                onRemove={() => removeData(index)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MultipleGeneratorLayout: React.FC<MultipleGeneratorLayoutProps> = ({
  storageKey,
  defaultData,
  maxItems,
  itemName,
  confirmClearMessage,
  RenderComponent,
}) => {
  const getInitialData = (): string[] => {
    try {
      const savedData = sessionStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        return Array.isArray(parsed) && parsed.length > 0
          ? parsed
          : defaultData;
      }
    } catch (error) {
      console.error("Error loading data from sessionStorage:", error);
    }
    return defaultData;
  };

  const [dataList, setDataList] = useState<string[]>(getInitialData);
  const [viewMode, setViewMode] = useViewMode();
  const [itemSize, setItemSize] = useItemSize();

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(dataList));
    } catch (error) {
      console.error("Error saving data to sessionStorage:", error);
    }
  }, [dataList, storageKey]);

  const addData = useCallback(() => {
    if (dataList.length < maxItems) {
      setDataList((prev) => [...prev, ""]);
    }
  }, [dataList.length, maxItems]);

  const removeData = useCallback(
    (index: number) => {
      if (dataList.length <= 1) return;
      setDataList((prev) => prev.filter((_, i) => i !== index));
    },
    [dataList.length]
  );

  const updateData = useCallback((index: number, value: string) => {
    setDataList((prev) =>
      prev.map((item, i) => (i === index ? value : item))
    );
  }, []);

  const clearAllData = useCallback(() => {
    setDataList([""]);
  }, []);

  const clearSingleData = useCallback((index: number) => {
    setDataList((prev) => prev.map((item, i) => (i === index ? "" : item)));
  }, []);

  return (
    <>
      <StickyAction
        onAdd={addData}
        currentCount={dataList.length}
        maxItems={maxItems}
        itemName={itemName}
        viewMode={viewMode}
        setViewMode={setViewMode}
        itemSize={itemSize}
        setItemSize={setItemSize}
        onClear={clearAllData}
        confirmMessage={confirmClearMessage}
      />

      <GeneratorList
        viewMode={viewMode}
        itemSize={itemSize}
        dataList={dataList}
        updateData={updateData}
        clearSingleData={clearSingleData}
        removeData={removeData}
        RenderComponent={RenderComponent}
      />
    </>
  );
};

export default MultipleGeneratorLayout;
